import React from "react"
import styled from "styled-components"
import { NeutralRawLink } from "../link/link.component"
import { QuaternaryHeading } from "../../../components/common/heading.component"
import ArrowIcon from "../../../images/arrow.inline.svg"
import { AccordionItem as AccordionItemProps } from "./accordion-item.interface"
import {
  AppFunctionComponent,
  ContentfulImage,
  GatsbyImage,
} from "../../../types"
import { Image } from "../../../components/common/image.component"

export const SingleAccordionItem = styled.li`
  position: relative;
  display: flex;
  flex-direction: column;
`

const ScrollHandler = styled.span`
  position: absolute;
  display: block;
  top: -80px;
  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    top: -110px;
  }
`

const Bar = styled(NeutralRawLink)`
  display: flex;
  justify-content: space-between;
  cursor: pointer;
`

const Title = styled(QuaternaryHeading)`
  font-family: ${({ theme }) => theme.font.basic};
  margin: 20px 0;
  font-weight: 500;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile - 1}px) {
    max-width: 270px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoint.mobile}px) {
    padding-right: 30px;
  }
`

const ArrowWrapper = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    transition: transform 0.5s;
    transform: rotate(${({ active }) => (active ? "270deg" : "90deg")});
    height: 16px;
    width: 16px;
  }
`

const Description = styled.div<{ active?: boolean }>`
  transition: all 0.3s ease-in-out;
  opacity: ${({ active }) => (active ? 1 : 0)};
  visibility: ${({ active }) => (active ? "visible" : "hidden")};
  max-height: ${({ active }) => (active ? "2500px" : 0)};

  font-size: ${14 / 16}rem;
  line-height: ${30 / 14}em;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    font-size: ${16 / 16}rem;
    line-height: ${32 / 16}em;
  }
`

const Content = styled.div`
  margin-bottom: 20px;
  text-align: left;
`

const SingleTextAccordionItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 32px;
    &:first-child {
      margin-top: 40px;
    }
  }
`

const ItemImageWrapper = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    flex: 1;
    width: 100%;
    align-items: center;
  }
`

export const ItemIcon = styled.img`
  width: 32px;
  height: 32px;
  margin-top: 0 !important;
`

export const ItemImage = styled(Image)`
  display: block;
  max-width: calc(var(--image-width) * 1px);
  width: 100%;
  img {
    object-fit: contain;
    object-position: center;
  }
`

const TextWithImageTitle = styled.h3`
  font-family: Facto;
  font-size: 23px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    text-align: center;

    font-family: Facto;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%; /* 26px */
  }
`

const TextWithImageContent = styled.p`
  font-family: Montserrat;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 27px */

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    text-align: center;

    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 21px */
  }
`

type ImageResolverProps = (GatsbyImage | ContentfulImage) & {
  imageWidth?: number
}

export const ItemImageResolver: AppFunctionComponent<ImageResolverProps> = (
  itemImage
) => {
  const isGatsbyImage = (
    image: ContentfulImage | GatsbyImage
  ): image is GatsbyImage => {
    return Boolean((image as GatsbyImage).gatsbyImageData)
  }

  return isGatsbyImage(itemImage) ? (
    <ItemImage image={itemImage.gatsbyImageData} alt={itemImage.description} />
  ) : (
    <ItemIcon src={itemImage.file.url} alt={itemImage.description} />
  )
}

const AccordionItem: AppFunctionComponent<AccordionItemProps> = ({
  link,
  slug,
  title,
  text,
  active,
  onClick,
  type,
  image,
}) => {
  if (type === "item with image") {
    return (
      <SingleTextAccordionItem>
        <ItemImageWrapper>
          {image && <ItemImageResolver {...(image as any)} />}
          <TextWrapper>
            <TextWithImageTitle>{title}</TextWithImageTitle>
            <TextWithImageContent>{text}</TextWithImageContent>
          </TextWrapper>
        </ItemImageWrapper>
      </SingleTextAccordionItem>
    )
  }

  return (
    <SingleAccordionItem>
      <ScrollHandler id={slug} />
      <Bar to={`${link}#${slug}`} onClick={onClick}>
        <Title>{title}</Title>
        <ArrowWrapper active={active}>
          <ArrowIcon aria-hidden="true" />
        </ArrowWrapper>
      </Bar>
      <Description active={active}>
        <Content>{text}</Content>
      </Description>
    </SingleAccordionItem>
  )
}

export default AccordionItem
